import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Upload, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { imgSrc } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const inputClass =
  "w-full rounded-[4px] border border-[#D8D6D0] bg-white px-3 py-2.5 text-[15px] text-[#111111] placeholder:text-[#A0A0A0] outline-none transition-colors duration-200 focus:border-[#111111]";
export const labelClass =
  "mb-1 block font-display text-[11px] font-bold uppercase tracking-[0.15em] text-[#555555]";
export const btnPrimary =
  "inline-flex items-center gap-2 rounded-[4px] bg-[#111111] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-cream hover:text-[#111111] disabled:opacity-60";
export const btnGhost =
  "inline-flex items-center gap-2 rounded-[4px] border border-[#D8D6D0] bg-white px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wide text-[#555555] transition-colors duration-200 hover:border-[#111111] hover:text-[#111111]";

export const Text = ({ label, value, onChange, testid, placeholder, type = "text" }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <input type={type} data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputClass} />
  </div>
);

export const Area = ({ label, value, onChange, rows = 4, testid, hint }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <textarea data-testid={testid} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputClass} resize-y`} />
    {hint && <p className="mt-1 text-xs text-[#8E8D8A]">{hint}</p>}
  </div>
);

export const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputClass}>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

// Lijst van losse regels (punten, werkzaamheden): één per regel.
export const Lines = ({ label, value, onChange, testid, hint = "Eén item per regel." }) => {
  const joined = (value || []).join("\n");
  const [text, setText] = useState(joined);
  useEffect(() => {
    setText((cur) => (cur.split("\n").filter((l) => l.trim() !== "").join("\n") === joined ? cur : joined));
  }, [joined]);
  return (
    <Area
      label={label}
      value={text}
      onChange={(v) => {
        setText(v);
        onChange(v.split("\n").filter((l) => l.trim() !== ""));
      }}
      rows={Math.max(3, text.split("\n").length + 1)}
      testid={testid}
      hint={hint}
    />
  );
};

export const Card = ({ title, children, actions }) => (
  <section className="border border-[#DDD9D0] bg-white">
    <header className="flex items-center justify-between border-b border-[#E2E0D9] px-5 py-3">
      <h2 className="font-display text-sm font-bold uppercase tracking-wide text-[#111111]">{title}</h2>
      {actions}
    </header>
    <div className="space-y-4 p-5">{children}</div>
  </section>
);

export const ImageField = ({ label, value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd);
      onChange({ src: data.url, alt: value?.alt || file.name.replace(/\.\w+$/, "") });
      toast.success("Foto geüpload");
    } catch (err) {
      toast.error("Uploaden mislukt", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-start gap-4">
        <div className="h-24 w-32 shrink-0 overflow-hidden border border-[#D8D6D0] bg-[#F5F4F1]">
          {value?.src && <img src={imgSrc(value.src)} alt={value.alt || ""} className="h-full w-full object-cover" />}
        </div>
        <div className="flex-1 space-y-2">
          <input type="text" value={value?.alt ?? ""} onChange={(e) => onChange({ ...value, alt: e.target.value })} placeholder="Beschrijving van de foto (alt-tekst)" className={inputClass} />
          <button type="button" data-testid={testid} onClick={() => ref.current?.click()} disabled={busy} className={btnGhost}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {value?.src ? "Andere foto kiezen" : "Foto uploaden"}
          </button>
          <input ref={ref} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
    </div>
  );
};

// Lijst van objecten (secties, faq, stappen, feiten) met per item een paar velden.
export const ObjectList = ({ label, value, onChange, fields, empty, testid }) => {
  const items = value || [];
  const set = (i, key, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className={labelClass}>{label}</span>
        <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...items, { ...empty }])} className={btnGhost}>
          <Plus className="h-3.5 w-3.5" /> Toevoegen
        </button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} data-testid={`${testid}-item-${i}`} className="relative space-y-2 border border-[#E2E0D9] bg-[#FAFAF8] p-3 pt-8">
            <div className="absolute right-2 top-2 flex items-center gap-1 text-[#8E8D8A]">
              <button type="button" aria-label="Omhoog" onClick={() => move(i, -1)} className="p-0.5 hover:text-[#111111] disabled:opacity-30" disabled={i === 0}><ArrowUp className="h-4 w-4" /></button>
              <button type="button" aria-label="Omlaag" onClick={() => move(i, 1)} className="p-0.5 hover:text-[#111111] disabled:opacity-30" disabled={i === items.length - 1}><ArrowDown className="h-4 w-4" /></button>
              <button type="button" aria-label="Verwijderen" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-0.5 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {fields.map((f) =>
              f.lines ? (
                <Lines key={f.key} label={f.label} value={it[f.key]} onChange={(v) => set(i, f.key, v)} hint={f.hint || "Eén item per regel."} />
              ) : f.multiline ? (
                <Area key={f.key} label={f.label} value={it[f.key]} onChange={(v) => set(i, f.key, v)} rows={f.rows || 3} />
              ) : (
                <Text key={f.key} label={f.label} value={it[f.key]} onChange={(v) => set(i, f.key, v)} />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
